import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Eye, ChevronLeft, ChevronRight, Download, Store, CheckCircle, DollarSign, Package, MapPin, Star, RotateCcw, XCircle, Clock, AlertTriangle, Plus, Upload, MessageCircle, UserCog } from 'lucide-react';
import DateFilter from '@/components/DateFilter';
import CreateVendorDialog from '@/components/admin/CreateVendorDialog';
import BulkImportDialog from '@/components/admin/BulkImportDialog';
import { useImpersonation } from '@/contexts/ImpersonationContext';

export default function AdminVendors() {
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState('all');
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  // Bulk selection state
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (activeView !== 'all') params.status = activeView;
      if (dateFilter) {
        if (typeof dateFilter === 'object' && dateFilter.type === 'custom') {
          params.date_from = dateFilter.from;
          params.date_to = dateFilter.to;
        } else {
          params.date_filter = dateFilter;
        }
      }
      const res = await api.get('/admin/vendors', { params });
      setVendors(res.data.vendors || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFilter, activeView]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/vendors/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
  }, []);

  useEffect(() => { 
    fetchVendors(); 
    fetchStats();
  }, [fetchVendors, fetchStats]);

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/vendors/export', { 
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendors_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Vendor data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setSearch('');
    setDateFilter(null);
    setActiveView('all');
    setPage(1);
    toast.success('Filters reset');
  };

  const handleImpersonateVendor = async (vendor) => {
    const success = await startImpersonation(vendor.id, vendor.name);
    if (success) {
      // Redirect to vendor dashboard
      navigate('/vendor');
    }
  };

  const statusBadge = (status) => {
    const s = (status || 'pending').toLowerCase();
    const map = { 
      approved: 'bg-emerald-50 text-emerald-800', 
      pending: 'bg-amber-50 text-amber-800', 
      rejected: 'bg-rose-50 text-rose-800', 
      suspended: 'bg-slate-100 text-slate-800' 
    };
    return <Badge variant="outline" className={`text-xs ${map[s] || 'bg-slate-100 text-slate-800'}`}>{s}</Badge>;
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedVendors.length === vendors.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(vendors.map(v => v.id));
    }
  };

  const toggleSelectVendor = (vendorId) => {
    if (selectedVendors.includes(vendorId)) {
      setSelectedVendors(selectedVendors.filter(id => id !== vendorId));
    } else {
      setSelectedVendors([...selectedVendors, vendorId]);
    }
  };

  const handleBulkAction = () => {
    if (!bulkAction) {
      toast.error('Please select an action');
      return;
    }
    if (selectedVendors.length === 0) {
      toast.error('Please select at least one vendor');
      return;
    }

    setConfirmAction({
      type: bulkAction,
      count: selectedVendors.length,
      onConfirm: executeBulkAction
    });
    setShowConfirmDialog(true);
  };

  const executeBulkAction = async () => {
    try {
      if (bulkAction === 'delete') {
        await Promise.all(selectedVendors.map(id => api.delete(`/admin/vendors/${id}`)));
        toast.success(`${selectedVendors.length} vendor(s) deleted`);
      } else if (bulkAction === 'export') {
        exportVendorsToCSV(selectedVendors);
        toast.success(`${selectedVendors.length} vendor(s) exported`);
        setSelectedVendors([]);
        setBulkAction('');
        setShowConfirmDialog(false);
        return;
      }

      setSelectedVendors([]);
      setBulkAction('');
      setShowConfirmDialog(false);
      await fetchVendors();
      await fetchStats();
    } catch (err) {
      toast.error('Bulk action failed');
    }
  };

  const exportVendorsToCSV = (vendorIds) => {
    const selectedData = vendors.filter(v => vendorIds.includes(v.id));
    const headers = ['Vendor ID', 'Business Name', 'Email', 'Phone', 'Category', 'Status', 'Total Revenue', 'Created At'];
    const rows = selectedData.map(v => [
      v.vendor_number || 'N/A',
      `"${(v.business_name || 'N/A').replace(/"/g, '""')}"`,
      v.email || 'N/A',
      v.phone || 'N/A',
      v.business_category || 'N/A',
      v.status || 'N/A',
      v.total_revenue || 0,
      new Date(v.created_at).toLocaleString()
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `r2yn-vendors-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        if (vendors.length > 0) {
          if (selectedVendors.length === vendors.length) {
            setSelectedVendors([]);
            toast.info('All vendors deselected');
          } else {
            setSelectedVendors(vendors.map(v => v.id));
            toast.info(`${vendors.length} vendors selected`);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [vendors, selectedVendors]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Vendor Management</h1>
          <p className="text-muted-foreground mt-1">Manage and analyze all platform vendors.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-vendor-button">
            <Plus className="h-4 w-4 mr-2" />
            Create Vendor
          </Button>
          <Button variant="outline" onClick={() => setBulkImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
          <Button onClick={handleExportAll} disabled={exporting} data-testid="export-all-vendors">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export All'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('all')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Vendors</CardTitle>
              <Store className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_vendors || 0}</div>
              <p className="text-xs text-muted-foreground">All registered vendors</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('approved')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active_vendors || 0}</div>
              <p className="text-xs text-muted-foreground">Active & operating</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('pending')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending_vendors || 0}</div>
              <p className="text-xs text-muted-foreground">Awaiting approval</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('rejected')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.rejected_vendors || 0}</div>
              <p className="text-xs text-muted-foreground">Application declined</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('suspended')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suspended</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.suspended_vendors || 0}</div>
              <p className="text-xs text-muted-foreground">Account suspended</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Filter Tabs */}
      <Tabs value={activeView} onValueChange={(value) => { setActiveView(value); setPage(1); }} className="w-full">
        <TabsList className="grid w-full grid-cols-5 h-auto p-1 bg-muted">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="vendor-tab-all"
          >
            All Vendors
          </TabsTrigger>
          <TabsTrigger 
            value="approved"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="vendor-tab-approved"
          >
            Approved
          </TabsTrigger>
          <TabsTrigger 
            value="pending"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="vendor-tab-pending"
          >
            Pending
          </TabsTrigger>
          <TabsTrigger 
            value="rejected"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="vendor-tab-rejected"
          >
            Rejected
          </TabsTrigger>
          <TabsTrigger 
            value="suspended"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="vendor-tab-suspended"
          >
            Suspended
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeView} className="mt-6">
          <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by business name, email, or phone..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="vendors-search-input"
              />
            </div>
            <Button variant="outline" onClick={() => { setSearch(''); setDateFilter(null); setActiveView('all'); setPage(1); toast.success('Filters reset'); }}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <DateFilter 
              value={dateFilter}
              onChange={(value) => { setDateFilter(value); setPage(1); }}
              className="w-[200px]"
            />
          </div>

          {/* Bulk Actions Bar */}
          {selectedVendors.length > 0 && (
            <Card className="border-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="font-medium">{selectedVendors.length} vendor(s) selected</span>
                  <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="border rounded px-3 py-2 text-sm">
                    <option value="">Select action</option>
                    <option value="export">Export to CSV</option>
                    <option value="delete">Delete Vendors</option>
                  </select>
                  <Button onClick={handleBulkAction} disabled={!bulkAction} size="sm">
                    {bulkAction === 'export' ? 'Export' : 'Apply'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedVendors([])}>
                    Clear Selection
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vendors-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-3 px-2 w-12">
                      <Checkbox 
                        checked={selectedVendors.length === vendors.length && vendors.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left py-3 px-2 font-medium">Vendor ID</th>
                    <th className="text-left py-3 px-2 font-medium">Vendor</th>
                    <th className="text-left py-3 px-2 font-medium">Category</th>
                    <th className="text-left py-3 px-2 font-medium">Address</th>
                    <th className="text-left py-3 px-2 font-medium">Rating</th>
                    <th className="text-left py-3 px-2 font-medium">Status</th>
                    <th className="text-right py-3 px-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map(vendor => (
                    <tr 
                      key={vendor.id} 
                      className="border-b last:border-0 hover:bg-[hsl(351,96%,31%,0.02)] hover:border-l-2 hover:border-l-[hsl(351,96%,31%,0.3)] transition-all duration-200"
                      data-testid={`vendor-row-${vendor.id}`}
                    >
                      <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedVendors.includes(vendor.id)}
                          onCheckedChange={() => toggleSelectVendor(vendor.id)}
                        />
                      </td>
                      <td className="py-3 px-2 font-mono text-xs cursor-pointer" onClick={() => navigate(`/admin/vendors/${vendor.id}`)}>
                        {vendor.vendor_number || 'N/A'}
                      </td>
                      <td className="py-3 px-2 cursor-pointer" onClick={() => navigate(`/admin/vendors/${vendor.id}`)}>
                        <div className="flex items-center gap-2">
                          {vendor.image_url ? (
                            <img src={vendor.image_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Store className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          <span className="font-medium">{vendor.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 capitalize">{vendor.category || 'N/A'}</td>
                      <td className="py-3 px-2 text-muted-foreground text-xs">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {vendor.address?.slice(0, 35) || 'N/A'}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs">{vendor.rating?.toFixed(1) || '0.0'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2">{statusBadge(vendor.status)}</td>
                      <td className="py-3 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => handleImpersonateVendor(vendor)}
                            data-testid={`impersonate-vendor-${vendor.id}`}
                            title="View as Vendor"
                          >
                            <UserCog className="h-3.5 w-3.5 text-purple-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => navigate(`/admin/chat?user=${vendor.owner_user_id || vendor.id}`)}
                            data-testid={`chat-vendor-${vendor.id}`}
                            title="Chat with Vendor"
                          >
                            <MessageCircle className="h-3.5 w-3.5 text-green-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => navigate(`/admin/vendors/${vendor.id}`)}
                            data-testid={`view-vendor-${vendor.id}`}
                            title="View Details"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(1, p - 1))} 
                  disabled={page === 1}
                  data-testid="vendors-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                  disabled={page === totalPages}
                  data-testid="vendors-next-page"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {confirmAction?.type === 'delete' && (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to <span className="font-bold text-rose-600">permanently delete {confirmAction.count} vendor(s)</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. All vendor data and history will be permanently removed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => confirmAction?.onConfirm()} 
              className={confirmAction?.type === 'delete' ? 'bg-rose-600 hover:bg-rose-700' : ''}
            >
              {confirmAction?.type === 'delete' ? 'Delete Vendors' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Vendor Dialog */}
      <CreateVendorDialog 
        open={createDialogOpen} 
        onOpenChange={setCreateDialogOpen}
        onSuccess={fetchVendors}
      />

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        entityType="vendors"
        onSuccess={fetchVendors}
      />
    </div>
  );
}
